import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export interface ScanAttempt {
   text: string;
   confidence: number;
   timestamp: Date;
   image?: string;
}

export interface ScannerState {
   scanning: boolean;
   status: string;
   attempts: ScanAttempt[];
   currentAttempt: number;
   maxAttempts: number;
   torchActive: boolean;
   torchSupported: boolean;
}

@Injectable({ providedIn: 'root' })
export class ScannerStateService {
   private state = new BehaviorSubject<ScannerState>({
      scanning: false,
      status: 'Ready',
      attempts: [],
      currentAttempt: 0,
      maxAttempts: 5,
      torchActive: false,
      torchSupported: false
   });

   get state$(): Observable<ScannerState> {
      return this.state.asObservable();
   }

   get currentState(): ScannerState {
      return this.state.value;
   }

   updateState(partialState: Partial<ScannerState>): void {
      this.state.next({ ...this.state.value, ...partialState });
   }

   resetState(): void {
      this.state.next({
         scanning: false,
         status: 'Ready',
         attempts: [],
         currentAttempt: 0,
         maxAttempts: 5,
         torchActive: false,
         torchSupported: false
      });
   }

   addScanAttempt(attempt: Omit<ScanAttempt, 'timestamp'>): void {
      const currentState = this.state.value;
      this.updateState({
         attempts: [
            { ...attempt, timestamp: new Date() },
            ...currentState.attempts
         ],
         currentAttempt: currentState.currentAttempt + 1
      });
   }

   select<T>(selector: (state: ScannerState) => T): Observable<T> {
      return this.state$.pipe(
         map(selector),
         distinctUntilChanged()
      );
   }
}